<template>
  <div class="w-full">
    <!-- Page Header -->
    <PageHeader 
      title="Gestion des codes promo" 
      subtitle="Gérez vos codes promotionnels et parrainage"
      class="mb-4" 
    />
    
    <div class="w-full px-6">
      <!-- Section Code de parrainage -->
      <ReferralCodeSection
        :referral-code="referralCode"
        @copy="copyReferralCode"
        @view-template="viewTemplate"
      />

      <!-- Section Ajout de code promo -->
      <div class="mb-8">
        <div class="flex items-center gap-3 mb-4">
          <ActionButton
            label="Ajouter un code promo"
            icon="fas fa-plus"
            variant="primary"
            @click="openAddPromoModal"
          />
          <span class="text-black font-medium">: Codes promotionnels personnalisés</span>
        </div>
      </div>

      <!-- Table des codes promo -->
      <div v-if="state.promoCodes.length === 0 && !state.isLoading" class="text-center py-8 text-black">
        Aucun code promo trouvé
      </div>
      <div v-else-if="state.isLoading" class="text-center py-8 text-black">
        Chargement...
      </div>
      <PromoCodeTable
        v-else
        :promo-codes="state.promoCodes"
        @delete="handleDeletePromoCode"
        @toggle-status="handleTogglePromoCodeStatus"
        @view="viewPromoCode"
      />

      <!-- Modal Ajout/Modification de code promo -->
      <AddPromoModal
        :is-visible="showAddModal"
        :is-submitting="isAddingPromo"
        @update:is-visible="showAddModal = $event"
        @submit="handleAddPromoCode"
      />

      <!-- Modal Aperçu Email Promo -->
      <ModalComp
        :isVisible="showViewModal"
        title="Aperçu de l'email promotionnel"
        size="xl"
        :hideFooter="true"
        @update:isVisible="showViewModal = $event"
      >
        <template #body>
          <div v-if="previewLoading" class="py-10 text-center text-black">Chargement de l'aperçu…</div>
          <div v-else-if="previewError" class="p-4 bg-red-50 text-red-700 rounded">{{ previewError }}</div>
          <div v-else>
            <div class="border rounded overflow-hidden preview-container">
              <iframe
                class="w-full preview-iframe"
                :style="{ height: '80vh' }"
                :srcdoc="previewHtml"
              ></iframe>
            </div>

            <!-- Informations techniques sous l'aperçu (图二) -->
            <div v-if="selectedPromoCode" class="mt-6 bg-gray-50 rounded-lg p-5 border border-gray-200">
              <div class="flex items-center gap-2 mb-4 text-black font-semibold">
                <i class="fas fa-cog text-black"></i>
                Informations techniques
              </div>
              <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <!-- Date de création -->
                <div class="flex items-center gap-3">
                  <div class="w-9 h-9 bg-gray-100 rounded-full flex items-center justify-center">
                    <i class="fas fa-calendar-plus text-black text-sm"></i>
                  </div>
                  <div>
                    <div class="text-xs text-black">Date de création</div>
                    <div class="font-semibold text-black">{{ selectedPromoCode.dateCreation }}</div>
                  </div>
                </div>

                <!-- Cumulable -->
                <div class="flex items-center gap-3">
                  <div class="w-9 h-9 bg-blue-100 rounded-full flex items-center justify-center">
                    <i class="fas" :class="selectedPromoCode.cumulable ? 'fa-link text-blue-600' : 'fa-unlink text-black'"></i>
                  </div>
                  <div>
                    <div class="text-xs text-black">Cumulable</div>
                    <div class="font-semibold" :class="selectedPromoCode.cumulable ? 'text-blue-700' : 'text-black'">{{ selectedPromoCode.cumulable ? 'Oui' : 'Non' }}</div>
                  </div>
                </div>

                <!-- Première commande -->
                <div class="flex items-center gap-3">
                  <div class="w-9 h-9 bg-green-100 rounded-full flex items-center justify-center">
                    <i class="fas fa-user-plus text-green-600 text-sm"></i>
                  </div>
                  <div>
                    <div class="text-xs text-black">Première commande</div>
                    <div class="font-semibold text-black">{{ selectedPromoCode.premiereCommande ? 'Oui' : 'Non' }}</div>
                  </div>
                </div>

                <!-- Offert : visible uniquement quand un admin consulte le compte vendeur -->
                <div v-if="authStore.isImpersonating" class="flex items-center gap-3">
                  <div class="w-9 h-9 bg-purple-100 rounded-full flex items-center justify-center">
                    <i class="fas fa-gift text-purple-600 text-sm"></i>
                  </div>
                  <div>
                    <div class="text-xs text-black">Offert</div>
                    <div class="font-semibold text-black">{{ selectedPromoCode.offert ? 'Oui' : 'Non' }}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </template>
      </ModalComp>

      <!-- Modal Template -->
      <ModalComp
        :isVisible="showTemplateModal"
        title="Aperçu du template"
        size="xl"
        @update:isVisible="showTemplateModal = false"
      >
        <template #body>
          <!-- Onglets de langue -->
          <div class="mb-6">
            <div class="border-b border-gray-200">
              <nav class="-mb-px flex space-x-8">
                <button
                  v-for="lang in languages"
                  :key="lang.code"
                  @click="selectedLanguage = lang.code"
                  :class="[
                    'py-2 px-1 border-b-2 font-medium text-sm',
                    selectedLanguage === lang.code
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-black hover:text-black hover:border-gray-300'
                  ]"
                >
                  {{ lang.name }}
                </button>
              </nav>
            </div>
          </div>

          <!-- Contenu du template selon la langue -->
          <div class="space-y-6">
            <div v-if="selectedLanguage === 'fr'" class="space-y-4">
              <h3 class="text-lg font-semibold text-black">Template Français</h3>
              <div class="bg-gray-50 p-4 rounded-lg">
                <p class="text-black">
                  Bonjour,<br><br>
                  Je vous propose une offre exclusive avec le code <strong>{{ referralCode }}</strong> qui vous donne -10% sur votre première commande chez Efashion Paris.<br><br>
                  Ce code est valable à vie pour votre première commande.<br><br>
                  Cordialement
                </p>
              </div>
            </div>

            <div v-if="selectedLanguage === 'en'" class="space-y-4">
              <h3 class="text-lg font-semibold text-black">English Template</h3>
              <div class="bg-gray-50 p-4 rounded-lg">
                <p class="text-black">
                  Hello,<br><br>
                  I'm offering you an exclusive deal with code <strong>{{ referralCode }}</strong> which gives you -10% on your first order at Efashion Paris.<br><br>
                  This code is valid for life for your first order.<br><br>
                  Best regards
                </p>
              </div>
            </div>

            <div v-if="selectedLanguage === 'es'" class="space-y-4">
              <h3 class="text-lg font-semibold text-black">Plantilla Española</h3>
              <div class="bg-gray-50 p-4 rounded-lg">
                <p class="text-black">
                  Hola,<br><br>
                  Te ofrezco una oferta exclusiva con el código <strong>{{ referralCode }}</strong> que te da -10% en tu primer pedido en Efashion Paris.<br><br>
                  Este código es válido de por vida para tu primer pedido.<br><br>
                  Saludos cordiales
                </p>
              </div>
            </div>

            <div v-if="selectedLanguage === 'it'" class="space-y-4">
              <h3 class="text-lg font-semibold text-black">Template Italiano</h3>
              <div class="bg-gray-50 p-4 rounded-lg">
                <p class="text-black">
                  Ciao,<br><br>
                  Ti offro un'offerta esclusiva con il codice <strong>{{ referralCode }}</strong> che ti dà -10% sul tuo primo ordine su Efashion Paris.<br><br>
                  Questo codice è valido a vita per il tuo primo ordine.<br><br>
                  Cordiali saluti
                </p>
              </div>
            </div>
          </div>
        </template>
      </ModalComp>

      <!-- Modal Alerte Doublon Code Promo -->
      <ModalComp
        :isVisible="showDuplicateModal"
        title="Attention"
        :alert="duplicateMessage"
        size="sm"
        @update:isVisible="showDuplicateModal = $event"
      >
        <template #footer>
          <button
            type="button"
            class="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            @click="showDuplicateModal = false"
          >
            OK
          </button>
        </template>
      </ModalComp>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, reactive, onMounted } from 'vue'
import { 
  PageHeader, 
  ActionButton, 
  ModalComp,
  PromoCodeTable,
  ReferralCodeSection,
  AddPromoModal
} from '@/components'
import type { PromoCode, CreatePromoCodeInput } from '@/types/promo'
import type { Buyer } from '@/types/buyer'
import { 
  getPromoCodes, 
  createPromoCode, 
  deletePromoCode as deletePromoCodeApi, 
  togglePromoCodeStatus as togglePromoCodeStatusApi 
} from '@/api/promoCodes'
import { getPromoEmailPreview } from '@/api/promoCodes'
import { getBuyersByVendor } from '@/api/buyers'
import { useAuthStore } from '@/stores/auth'
import { remiseLocalisationLabel } from '@/utils/remiseLocalisation'
import {useRouter} from "vue-router";

// Store d'authentification
const authStore = useAuthStore()
const router = useRouter()
// State local
const state = reactive({
  promoCodes: [] as PromoCode[], // Données formatées
  buyers: [] as Buyer[],
  isLoading: false,
  error: null as string | null
})



const fetchPromoCodes = async () => {
  state.isLoading = true
  state.error = null
  
  try {
    // Récupérer l'ID du vendeur connecté
    const vendeurId = authStore.user?.id_vendeur
    
    if (!vendeurId) {
      state.error = 'Utilisateur non connecté'
      return
    }
    
    // Récupérer les codes promo (prioritaire) et les acheteurs (best‑effort)
    const promoCodesApi = await getPromoCodes(vendeurId)
    let buyersData: Buyer[] = []
    try {
      buyersData = await getBuyersByVendor()
    } catch (e) {
      // Ne pas bloquer l'affichage des codes promo si la récupération des acheteurs échoue
      console.warn('[PromoCode] Impossible de récupérer la liste des acheteurs, utilisation des libellés par défaut')
      buyersData = []
    }
    
    // Mapping direct comme dans les remises (plus rapide)
    state.promoCodes = promoCodesApi.map((promo: any) => {
      // Trouver l'acheteur correspondant
      const buyer = buyersData.find(
        (b) => String(b.id_acheteur) === String(promo.id_acheteur ?? ''),
      )
      const hasBuyer = promo.id_acheteur != null && Number(promo.id_acheteur) > 0
      const acheteurInfo = hasBuyer
        ? (promo.acheteurNom || buyer?.nomSociete || `Acheteur #${promo.id_acheteur}`)
        : 'Tous les acheteurs'
      
      return {
        id: promo.id_code_promo.toString(),
        code: promo.libelle || '',
        acheteur: acheteurInfo,
        idAcheteur: hasBuyer ? String(promo.id_acheteur) : null,
        buyerEmail: promo.acheteurEmail || buyer?.email || null,
        type: (promo.type === 'pourcentage' ? 'Percentage' : 'Reduction') as 'Percentage' | 'Reduction',
        montant: promo.type === 'pourcentage' ? `${promo.montant}%` : `${promo.montant}€`,
        montantMin: promo.minimum ? promo.minimum.toString() : '0',
        // Le backend peut renvoyer des nombres ou des chaînes
        localisation: remiseLocalisationLabel(promo.localisation),
        utilisation: ((promo.utilisation === 1 || promo.utilisation === '1') ? 'unique' : 'multiple') as 'unique' | 'multiple',
        actif: promo.actif ? 'activé' : 'désactivé',
        dateDebut: promo.date_debut ? new Date(promo.date_debut).toLocaleDateString('fr-FR') : 'Non définie',
        dateFin: promo.date_fin ? new Date(promo.date_fin).toLocaleDateString('fr-FR') : 'Non définie',
        dateCreation: promo.date_creation ? new Date(promo.date_creation).toLocaleDateString('fr-FR') : 'Non définie',
        // Champs additionnels persistés côté DB
        premiereCommande: !!(promo as any).premiere_commande,
        offert: !!(promo as any).offert,
        cumulable: !!(promo as any).cumulable,
        // Dates brutes pour calcul du statut
        dateDebutISO: promo.date_debut ?? null,
        dateFinISO: promo.date_fin ?? null
      }
    })
    
    if (state.promoCodes.length === 0) {
      console.debug('[PromoCode] Aucun code promo pour vendeur', vendeurId, '— payload brut:', promoCodesApi)
    }
    
    state.buyers = buyersData
  } catch (error) {
    state.error = 'Erreur lors du chargement des codes promo'
    // En cas d'erreur, initialiser avec un tableau vide
    state.promoCodes = []
  } finally {
    state.isLoading = false
  }
}

const addPromoCode = async (promoData: CreatePromoCodeInput) => {
  state.isLoading = true
  state.error = null
  
  try {
    const vendeurId = authStore.user?.id_vendeur
    
    if (!vendeurId) {
      state.error = 'Utilisateur non connecté'
      return
    }
    
    const newPromo = await createPromoCode(promoData, vendeurId)
    state.promoCodes.unshift(newPromo)
  } catch (error) {
    state.error = 'Erreur lors de l\'ajout du code promo'
  } finally {
    state.isLoading = false
  }
}

const handleDeletePromoCode = async (row: PromoCode) => {
  state.isLoading = true
  state.error = null
  
  try {
    await deletePromoCodeApi(row.id)
    
    const index = state.promoCodes.findIndex(promo => promo.id === row.id)
    if (index !== -1) {
      state.promoCodes.splice(index, 1)
    }
  } catch (error) {
    state.error = 'Erreur lors de la suppression du code promo'
  } finally {
    state.isLoading = false
  }
}

const handleTogglePromoCodeStatus = async (row: PromoCode) => {
  try {
    const isCurrentlyActive = row.actif === 'activé'
    const newStatus = !isCurrentlyActive
    
    await togglePromoCodeStatusApi(row.id, newStatus)
    
    // Mettre à jour l'état local
    row.actif = newStatus ? 'activé' : 'désactivé'
  } catch (err) {
    // Erreur silencieuse pour le changement de statut
  }
}
// UI State
const showAddModal = ref(false)
const showViewModal = ref(false)
const previewHtml = ref<string>('')
const previewLoading = ref(false)
const previewError = ref<string>('')
const showTemplateModal = ref(false)
const showDuplicateModal = ref(false)
const duplicateMessage = ref('Ce code promo existe déjà. Veuillez choisir un autre nom.')
const selectedLanguage = ref('fr')
const referralCode = ref('EFP10-FDARO')
const selectedPromoCode = ref<PromoCode | null>(null)
const isAddingPromo = ref(false)

// Langues disponibles pour le template
const languages = [
  { code: 'fr', name: 'Français' },
  { code: 'en', name: 'Anglais' },
  { code: 'es', name: 'Espagnol' },
  { code: 'it', name: 'Italien' }
]

// Méthodes UI
const openAddPromoModal = () => {
  showAddModal.value = true
}

const handleAddPromoCode = async (formData: Record<string, any>) => {
  try {
    // Duplicate name restriction: prevent adding code with same name (case-insensitive, trimmed)
    const newCode = String(formData.code || '').trim().toLowerCase()
    const exists = state.promoCodes.some(pc => String(pc.code || '').trim().toLowerCase() === newCode)
    if (exists) {
      duplicateMessage.value = 'Ce code promo existe déjà. Veuillez choisir un autre nom.'
      showDuplicateModal.value = true
      return
    }

    isAddingPromo.value = true
    
    const type = formData.type === 'percentage' ? 'Percentage' : 'Reduction'
    const montant = parseFloat(formData.montant) || 0
    const montantMin = parseFloat(formData.montantMin) || 0
    
    const promoData: CreatePromoCodeInput = {
      code: formData.code || '',
      acheteur: formData.acheteur || 'tous',
      type: type as 'Percentage' | 'Reduction',
      montant: type === 'Percentage' ? `${montant.toFixed(2)}%` : `${montant.toFixed(2)}€`,
      montantMin: montantMin === 0 ? '0' : montantMin.toFixed(2),
      localisation: formData.localisation || '1',
      utilisation: (formData.utilisation as 'unique' | 'multiple') || 'unique',
      actif: formData.actif === 'true' ? 'activé' : 'désactivé',
      dateDebut: formData.dateDebut || new Date().toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' }),
      dateFin: formData.dateFin || new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' }),
      premiereCommande: formData.premiereCommande || false,
      offert: formData.offert || false,
      cumulable: formData.cumulable !== undefined ? formData.cumulable : true
    }
    
    
    await addPromoCode(promoData)
    showAddModal.value = false
  } catch (err) {
    // Erreur gérée par addPromoCode
  } finally {
    isAddingPromo.value = false
  }
}


const copyReferralCode = async () => {
  try {
    await navigator.clipboard.writeText(referralCode.value)
  } catch (error) {
    // Erreur silencieuse pour la copie
  }
}

const viewTemplate = () => {
  showTemplateModal.value = true
}

const viewPromoCode = async (row: PromoCode) => {
  selectedPromoCode.value = row
  showViewModal.value = true
  previewLoading.value = true
  previewError.value = ''
  previewHtml.value = ''

  try {
    const buyerFirstName = (row.acheteur || '').split(' ')[0] || 'Client'
    const sellerName = authStore.user?.nomBoutique || 'Votre grossiste'
    // Conserver les décimales pour éviter des erreurs comme 1.05% → 105%
    const montantStr = String(row.montant)
    const montantMatch = montantStr.match(/\d+(?:[.,]\d+)?/)
    const montantLabel = (montantMatch ? montantMatch[0] : '').replace(',', '.')
    const discountLabel = row.type === 'Percentage'
      ? `-${montantLabel}%`
      : `${montantLabel}€`
    const promoCode = row.code
    const endDate = row.dateFin || '—'
    const catalogueUrl = window.location.origin

    previewHtml.value = await getPromoEmailPreview({
      buyerFirstName,
      sellerName,
      discountLabel,
      promoCode,
      endDate,
      catalogueUrl,
    })
  } catch (e: any) {
    previewError.value = e?.message || 'Impossible de générer l\'aperçu pour le moment.'
  } finally {
    previewLoading.value = false
  }
}

onMounted(async() => {

  if (!authStore.isInitialized) {
    await authStore.checkAuth()
  }

  if (!authStore.isAuthenticated) {
    router.push({ name: 'auth' })
    return
  }
  fetchPromoCodes()
})
</script>

<style lang="scss" scoped>
button {
  transition: all 0.2s ease;
}

button:hover {
  transform: translateY(-1px);
}

.preview-container {
  overflow: hidden;
}

.preview-iframe {
  border: none;
  display: block;

  /* Custom scrollbar for the iframe container (if needed) and to influence the iframe's own scrollbar if possible via parent */
  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: #ccc;
    border-radius: 10px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: #aaa;
  }
}
</style>